import React, { useCallback, useEffect, useState } from "react";
import Layout, { PageHeader, Surface, StatusPill } from "@/components/Layout";
import TablePagination from "@/components/TablePagination";
import { api } from "@/lib/api";

const fmt$ = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PAGE_SIZE = 25;

export default function Transactions() {
  const [items, setItems] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [merchantId, setMerchantId] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/merchants", { params: { limit: 500 } }).then((r) => setMerchants(r.data.items));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (merchantId) params.merchant_id = merchantId;
      const r = await api.get("/admin/transactions", { params });
      setItems(r.data.items);
      setTotal(r.data.total ?? 0);
      setPages(r.data.pages ?? 0);
    } finally {
      setLoading(false);
    }
  }, [merchantId, page, limit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [merchantId, limit]);

  const byId = Object.fromEntries(merchants.map(m => [m.id, m.company_name]));

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="transactions-page">
        <PageHeader title="Transactions" subtitle="Live ledger across all issued cards." />

        <Surface className="p-3 mb-4 flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">Merchant</span>
          <select
            data-testid="tx-merchant-filter"
            value={merchantId}
            onChange={(e) => setMerchantId(e.target.value)}
            className="h-8 text-xs px-2 border border-zinc-200 rounded-sm bg-white focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none"
          >
            <option value="">All</option>
            {merchants.map(m => <option key={m.id} value={m.id}>{m.company_name}</option>)}
          </select>
          <div className="ml-auto text-xs text-zinc-500 tabular-nums">{total} txns</div>
        </Surface>

        <Surface>
          <div className="table-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
                <th className="px-5 py-3">When</th>
                <th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">Merchant</th>
                <th className="px-5 py-3">POS / Origin</th>
                <th className="px-5 py-3">Card</th>
                <th className="px-5 py-3 text-right">Amount</th>
                <th className="px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-xs text-zinc-500">Loading…</td></tr>
              )}
              {!loading && items.map((t) => (
                <tr key={t.id} className="border-b border-zinc-50">
                  <td className="px-5 py-3 text-xs font-mono text-zinc-500">{(t.created_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="px-5 py-3"><span className="text-[10px] font-mono uppercase tracking-[0.05em] px-1.5 py-0.5 rounded-sm bg-zinc-100">{t.type}</span></td>
                  <td className="px-5 py-3 text-xs">{byId[t.merchant_id] || t.merchant_id.slice(0, 8)}</td>
                  <td className="px-5 py-3 text-xs">{t.merchant_name || "—"}</td>
                  <td className="px-5 py-3 font-mono text-xs">{t.card_id?.slice(0, 8)}…</td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums">{fmt$(t.amount)}</td>
                  <td className="px-5 py-3"><StatusPill value={t.status} /></td>
                </tr>
              ))}
              {!loading && items.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-xs text-zinc-500">No transactions yet.</td></tr>}
            </tbody>
          </table>
          </div>
          <TablePagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            loading={loading}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </Surface>
      </div>
    </Layout>
  );
}
